import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Settings, Mic, Volume2, MessageSquare } from 'lucide-react';
import { NeMoTrainingConfig, NeMoPretrainedModel, NeMoTaskType, NeMoLoRAConfig as LoRAConfigType } from '@/mock/gpu-instance';
import NeMoLoRAConfig from './NeMoLoRAConfig';

interface NeMoConfigBuilderProps {
  config: NeMoTrainingConfig;
  onChange: (config: NeMoTrainingConfig) => void;
  selectedModel?: NeMoPretrainedModel;
  selectedTask?: NeMoTaskType;
}

const NeMoConfigBuilder = ({
  config,
  onChange,
  selectedModel,
  selectedTask,
}: NeMoConfigBuilderProps) => {
  const handleChange = <K extends keyof NeMoTrainingConfig>(
    key: K,
    value: NeMoTrainingConfig[K]
  ) => {
    onChange({ ...config, [key]: value });
  };

  const handleLoRAConfigChange = (loraConfig: LoRAConfigType) => {
    handleChange('loraConfig', loraConfig);
  };

  const showLoRAConfig = config.method === 'lora' || config.method === 'qlora';
  const showLLMConfig = selectedTask === 'llm';
  const showASRConfig = selectedTask === 'asr';
  const showTTSConfig = selectedTask === 'tts';

  return (
    <div className="space-y-6">
      {/* Basic Training Parameters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Training Parameters
          </CardTitle>
          <CardDescription>Configure basic training hyperparameters</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Epochs */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Epochs</Label>
              <span className="text-sm text-muted-foreground font-mono">{config.epochs}</span>
            </div>
            <Slider
              value={[config.epochs]}
              onValueChange={([value]) => handleChange('epochs', value)}
              min={1}
              max={showLLMConfig ? 10 : 200}
              step={1}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1</span>
              <span>{showLLMConfig ? '10' : '200'}</span>
            </div>
          </div>

          {/* Batch Size */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Batch Size</Label>
              <span className="text-sm text-muted-foreground font-mono">{config.batchSize}</span>
            </div>
            <Slider
              value={[config.batchSize]}
              onValueChange={([value]) => handleChange('batchSize', value)}
              min={1}
              max={showLLMConfig ? 16 : 64}
              step={1}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1</span>
              <span>{showLLMConfig ? '16' : '64'}</span>
            </div>
          </div>

          {/* Learning Rate */}
          <div className="space-y-2">
            <Label>Learning Rate</Label>
            <Select
              value={config.learningRate.toString()}
              onValueChange={(value) => handleChange('learningRate', parseFloat(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0.001">1e-3 (High)</SelectItem>
                <SelectItem value="0.0002">2e-4 (LoRA Recommended)</SelectItem>
                <SelectItem value="0.0001">1e-4 (Default)</SelectItem>
                <SelectItem value="0.00005">5e-5 (Fine-tuning)</SelectItem>
                <SelectItem value="0.00001">1e-5 (Very Low)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Optimizer */}
          <div className="space-y-2">
            <Label>Optimizer</Label>
            <Select
              value={config.optimizer}
              onValueChange={(value) => handleChange('optimizer', value as NeMoTrainingConfig['optimizer'])}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="adamw">AdamW (Recommended)</SelectItem>
                <SelectItem value="adam">Adam</SelectItem>
                <SelectItem value="fused_adam">Fused Adam (Faster)</SelectItem>
                <SelectItem value="sgd">SGD with Momentum</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Scheduler */}
          <div className="space-y-2">
            <Label>Learning Rate Scheduler</Label>
            <Select
              value={config.scheduler}
              onValueChange={(value) => handleChange('scheduler', value as NeMoTrainingConfig['scheduler'])}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cosine">Cosine Annealing</SelectItem>
                <SelectItem value="warmup_cosine">Warmup + Cosine (Recommended)</SelectItem>
                <SelectItem value="constant">Constant</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* LLM-Specific Config */}
      {showLLMConfig && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              LLM Configuration
            </CardTitle>
            <CardDescription>Settings specific to language model training</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Sequence Length */}
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label>Sequence Length</Label>
                <span className="text-sm text-muted-foreground font-mono">
                  {config.sequenceLength?.toLocaleString() || 4096}
                </span>
              </div>
              <Select
                value={(config.sequenceLength || 4096).toString()}
                onValueChange={(value) => handleChange('sequenceLength', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2048">2,048 (Low VRAM)</SelectItem>
                  <SelectItem value="4096">4,096 (Recommended)</SelectItem>
                  <SelectItem value="8192">8,192</SelectItem>
                  <SelectItem value="16384">16,384 (High VRAM)</SelectItem>
                  <SelectItem value="32768">32,768 (Very High VRAM)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sequence Packing */}
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Sequence Packing</div>
                <div className="text-xs text-muted-foreground">
                  Pack multiple sequences into one for efficiency
                </div>
              </div>
              <Switch
                checked={config.sequencePacking ?? true}
                onCheckedChange={(checked) => handleChange('sequencePacking', checked)}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* ASR-Specific Config */}
      {showASRConfig && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Mic className="h-4 w-4" />
              ASR Configuration
            </CardTitle>
            <CardDescription>Settings specific to speech recognition training</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* SpecAugment */}
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">SpecAugment</div>
                <div className="text-xs text-muted-foreground">
                  Apply spectrogram augmentation for robustness
                </div>
              </div>
              <Switch
                checked={config.specAugment ?? true}
                onCheckedChange={(checked) => handleChange('specAugment', checked)}
              />
            </div>

            {/* Speed Perturbation */}
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Speed Perturbation</div>
                <div className="text-xs text-muted-foreground">
                  Vary audio playback speed (0.9x - 1.1x)
                </div>
              </div>
              <Switch
                checked={config.speedPerturbation ?? false}
                onCheckedChange={(checked) => handleChange('speedPerturbation', checked)}
              />
            </div>

            {/* Decoder Type */}
            <div className="space-y-2">
              <Label>Decoder Type</Label>
              <Select
                value={config.decoderType || 'ctc'}
                onValueChange={(value) => handleChange('decoderType', value as 'ctc' | 'rnnt' | 'transducer')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ctc">CTC (Connectionist Temporal Classification)</SelectItem>
                  <SelectItem value="rnnt">RNN-Transducer (Streaming)</SelectItem>
                  <SelectItem value="transducer">Transducer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* TTS-Specific Config */}
      {showTTSConfig && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Volume2 className="h-4 w-4" />
              TTS Configuration
            </CardTitle>
            <CardDescription>Settings specific to text-to-speech training</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Sample Rate */}
            <div className="space-y-2">
              <Label>Sample Rate</Label>
              <Select
                value={(config.sampleRate || 22050).toString()}
                onValueChange={(value) => handleChange('sampleRate', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="16000">16,000 Hz (Low quality)</SelectItem>
                  <SelectItem value="22050">22,050 Hz (Standard)</SelectItem>
                  <SelectItem value="44100">44,100 Hz (High quality)</SelectItem>
                  <SelectItem value="48000">48,000 Hz (Studio)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Speaker Embedding */}
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Speaker Embedding</div>
                <div className="text-xs text-muted-foreground">
                  Enable multi-speaker support
                </div>
              </div>
              <Switch
                checked={config.speakerEmbedding ?? false}
                onCheckedChange={(checked) => handleChange('speakerEmbedding', checked)}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* LoRA Configuration */}
      {showLoRAConfig && config.loraConfig && (
        <NeMoLoRAConfig
          config={config.loraConfig}
          onChange={handleLoRAConfigChange}
          isQLoRA={config.method === 'qlora'}
        />
      )}

      {/* Config Summary */}
      <Card className="bg-muted/30">
        <CardContent className="pt-4">
          <div className="text-sm font-medium mb-2">Configuration Summary</div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="text-muted-foreground">Method:</div>
            <div className="font-mono capitalize">{config.method}</div>
            <div className="text-muted-foreground">Epochs:</div>
            <div className="font-mono">{config.epochs}</div>
            <div className="text-muted-foreground">Batch Size:</div>
            <div className="font-mono">{config.batchSize}</div>
            <div className="text-muted-foreground">Learning Rate:</div>
            <div className="font-mono">{config.learningRate}</div>
            <div className="text-muted-foreground">Optimizer:</div>
            <div className="font-mono capitalize">{config.optimizer}</div>
            {showLLMConfig && (
              <>
                <div className="text-muted-foreground">Sequence Length:</div>
                <div className="font-mono">{config.sequenceLength?.toLocaleString() || 4096}</div>
              </>
            )}
            {showLoRAConfig && config.loraConfig && (
              <>
                <div className="text-muted-foreground">LoRA Rank:</div>
                <div className="font-mono">{config.loraConfig.rank}</div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NeMoConfigBuilder;
